import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { Sites } from '@enums/sites';
import { environment } from '@environments/environment';
import { ConsultationRequestError } from '@errors/consultation-request-error';
import { ConsultationRequestExpiredSessionError } from '@errors/consultation-request-expired-session-error';
import { ConsultationRequestFormAlreadySubmittedError } from '@errors/consultation-request-form-already-submitted-error';
import { ConsultationRequestInvalidLinkError } from '@errors/consultation-request-invalid-link-error';
import { ConsultationRequestOrderDetail } from '@models/consultation-request/consultation-request-order-detail';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { LoadingService } from '@services/loading.service';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ConsultationRequestResolver {
  private consultationRequestService = inject(ConsultationRequestService);
  private loadingService = inject(LoadingService);
  private errorLoggingService = inject(ErrorLoggingService);

  public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    this.loadingService.toggleLoader(true);
    const treatmentType = this.getTreatmentType(route);

    if (!route.queryParams.order_id || !route.queryParams.hash) {
      const error = new ConsultationRequestExpiredSessionError();

      return of({ error, errorMessage: error.message });
    }

    this.consultationRequestService.deletePreviousConsultationDbs(route.queryParams.order_id).subscribe();

    return this.consultationRequestService
      .getConsultationOrderDetails(
        route.queryParams.order_id,
        decodeURIComponent(route.queryParams.hash),
        treatmentType,
        this.urlHasPat(route) ? {} : this.getHeaderForRequestingPAT(),
      )
      .pipe(
        switchMap((orderDetail: ConsultationRequestOrderDetail) => {
          if (orderDetail.subscription_current_intake_url) {
            window.location.href = orderDetail.subscription_current_intake_url;

            return EMPTY;
          }

          return of(orderDetail);
        }),
        tap(() => this.consultationRequestService.saveConsultationOrderDetailToLocalStorage()),
        tap((data: ConsultationRequestOrderDetail) => this.storeAttachmentUploadPat(route, data)),
        catchError((error: any) => this.handleConsultationRequestError(error, route)),
      );
  }

  /**
   * Handles errors that occur during the consultation request process.
   *
   * @param {any} error The error object containing error details.
   * @param {ActivatedRouteSnapshot} route The activated route snapshot containing route parameters.
   */
  private handleConsultationRequestError(error: any, route: ActivatedRouteSnapshot) {
    let errorMessage =
      error instanceof ConsultationRequestError
        ? error.message
        : new ConsultationRequestInvalidLinkError(environment.email).message;

    if (
      error?.error?.code === 403 &&
      error?.error?.message &&
      !error.error.message.includes('We are very sorry, it seems like there was a problem.')
    ) {
      errorMessage = error.error.message;
    }

    this.logConsultationRequestError(error, route);

    return of({ error, errorMessage });
  }

  /**
   * Logs the consultation request error to the error logging service.
   *
   * @param {any} error The error object containing error details.
   * @param {ActivatedRouteSnapshot} route The activated route snapshot containing route parameters.
   */
  private logConsultationRequestError(error: any, route: ActivatedRouteSnapshot) {
    if (error instanceof ConsultationRequestFormAlreadySubmittedError) {
      return;
    }

    //@todo remove this after fixing the hash problem. Issue: https://github.com/Medology/ng-checkout/issues/2263
    this.errorLoggingService.logError({
      message: `Consultation Request Error: ${error.message}`,
      transactionId: route.queryParams.order_id ?? 'transactionId not found',
      hash: route.queryParams.hash ?? 'hash not found',
      decodedHash: decodeURIComponent(route.queryParams.hash) ?? 'hash not found',
    });
  }

  /**
   * Constructs the header object for requesting a Personal Access Token (PAT)
   *
   * @returns {Record<string, string>} Returns header for requesting an API PAT
   */
  private getHeaderForRequestingPAT(): Record<string, string> {
    return { [this.consultationRequestService.patRequestHeaderName]: 'true' };
  }

  /**
   * Determines the treatment type based on the route parameters.
   *
   * @param {ActivatedRouteSnapshot} route The activated route snapshot containing route parameters.
   */
  private getTreatmentType(route: ActivatedRouteSnapshot): ConsultationTreatmentTypes {
    let treatmentType: ConsultationTreatmentTypes = route.params.treatmentType.replace(/-/g, '_');

    if (treatmentType === ConsultationTreatmentTypes.Std && (environment.site as Sites) === Sites.Starfish) {
      treatmentType = ConsultationTreatmentTypes.StarfishStd;
    }

    return treatmentType;
  }

  /**
   * Stores the Personal Access Token (PAT) for the consultation questionnaire in the consultation request service.
   *
   * @param {ActivatedRouteSnapshot} route The activated route snapshot containing route parameters.
   * @param {ConsultationRequestOrderDetail} consultationOrderDetail Consultation order Details.
   */
  private storeAttachmentUploadPat(
    route: ActivatedRouteSnapshot,
    consultationOrderDetail: ConsultationRequestOrderDetail,
  ): void {
    this.consultationRequestService.saveAttachmentToken(route.fragment || consultationOrderDetail.anonymousToken || '');
  }

  /**
   * Checks if the URL contains a Personal Access Token (PAT) in the fragment.
   *
   * @param {ActivatedRouteSnapshot} route The activated route snapshot containing route parameters.
   */
  private urlHasPat(route: ActivatedRouteSnapshot): boolean {
    return !!route.fragment;
  }
}
